import { sidebarNavItemVariants } from '@/components/ui/button-variants';
import { IconButton } from '@/components/ui/IconButton';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import { PeriodBar } from '@/components/PeriodBar';
import { NAV_ITEMS } from '@/config/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useOrg } from '@/contexts/OrgContext';
import { useMemberAccess } from '@/hooks/useMemberAccess';
import type { ModuleId } from '@finvox/shared/member-access';
import { useSidebarLayout } from '@/contexts/SidebarLayoutContext';
import { cn } from '@/lib/cn';
import { LogOut, PanelLeft, PanelLeftClose, UserRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

function accountInitial(email: string): string {
  const local = email.trim().split('@')[0] ?? '';
  return (local.charAt(0) || '?').toUpperCase();
}

export function Sidebar() {
  const { t } = useTranslation();
  const { signOutUser, switchAccount, user } = useAuth();
  const { brandName, brandingLogoUrl } = useOrg();
  const memberAccess = useMemberAccess();
  const { collapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useSidebarLayout();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const mql = window.matchMedia('(min-width: 768px)');
    if (mql.matches) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileOpen, setMobileOpen]);

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-label={t('layout.sidebar.closeMenu')}
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <aside
        className={cn(
          'flex h-full min-h-0 flex-col overflow-hidden border-e border-[var(--fv-border)]',
          'max-md:fixed max-md:inset-y-0 max-md:start-0 max-md:z-50 max-md:w-60',
          'max-md:transition-transform max-md:duration-200 max-md:ease-out',
          mobileOpen
            ? 'max-md:translate-x-0'
            : 'max-md:pointer-events-none max-md:-translate-x-full rtl:max-md:translate-x-full',
          'md:relative md:shrink-0 md:translate-x-0 md:pointer-events-auto',
          collapsed ? 'md:w-[4.5rem]' : 'md:w-60',
          'flex'
        )}
        style={{ background: 'var(--fv-sidebar)' }}
        aria-hidden={isMobile && !mobileOpen ? true : undefined}
      >
        <div
          className={cn(
            'flex shrink-0 border-b border-white/10 py-4',
            collapsed
              ? 'items-center gap-3 px-4 md:flex-col md:items-center md:gap-2 md:px-2'
              : 'items-center gap-3 px-4'
          )}
        >
          {brandingLogoUrl ? (
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-white/15 bg-white/10">
              <img src={brandingLogoUrl} alt="" className="h-full w-full object-cover object-center" />
            </div>
          ) : (
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--fv-primary)]/20 text-sm font-bold text-white"
              aria-hidden
            >
              {brandName.charAt(0).toUpperCase()}
            </div>
          )}
          <p
            className={cn(
              'min-w-0 flex-1 truncate text-base font-bold leading-tight text-white',
              collapsed && 'md:hidden'
            )}
          >
            {brandName}
          </p>
          <IconButton
            variant="ghost"
            className="hidden shrink-0 text-[var(--fv-sidebar-text)] hover:bg-white/10 hover:text-white md:inline-flex"
            aria-label={collapsed ? t('layout.sidebar.expand') : t('layout.sidebar.collapse')}
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
          >
            {collapsed ? (
              <PanelLeft className="h-4 w-4" aria-hidden />
            ) : (
              <PanelLeftClose className="h-4 w-4" aria-hidden />
            )}
          </IconButton>
          <IconButton
            variant="ghost"
            className="shrink-0 text-[var(--fv-sidebar-text)] hover:bg-white/10 hover:text-white md:hidden"
            aria-label={t('layout.sidebar.closeMenu')}
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-4 w-4" aria-hidden />
          </IconButton>
        </div>

        {collapsed ? (
          <div className="hidden shrink-0 space-y-1 border-b border-white/10 px-2 py-2 md:block">
            <OrgSwitcher variant="sidebarCompact" />
            <PeriodBar variant="sidebarCompact" />
          </div>
        ) : (
          <>
            <OrgSwitcher variant="sidebar" />
            <PeriodBar variant="sidebar" />
          </>
        )}

        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-3">
          {NAV_ITEMS.filter((item) => memberAccess?.modules[item.id as ModuleId] ?? item.enabled).map(
            (item) => {
              const Icon = item.icon;
              const label = t(item.labelKey);
              if (!item.enabled) {
                return (
                  <div
                    key={item.id}
                    className={cn(
                      sidebarNavItemVariants({ disabled: true }),
                      collapsed && 'md:justify-center md:px-2'
                    )}
                    title={t('nav.comingSoon')}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    <span className={cn('flex-1', collapsed && 'md:hidden')}>{label}</span>
                    <span className={cn('text-[10px] uppercase tracking-wide', collapsed && 'md:hidden')}>
                      {t('nav.comingSoon')}
                    </span>
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  end={item.path === '/'}
                  title={collapsed ? label : undefined}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      sidebarNavItemVariants({ active: isActive }),
                      collapsed && 'md:justify-center md:px-2'
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className={cn(collapsed && 'md:hidden')}>{label}</span>
                </NavLink>
              );
            }
          )}
        </nav>

        <div className="shrink-0 border-t border-white/10 p-3">
          {user?.email ? (
            <div
              className={cn(
                'mb-1 flex items-center gap-3 rounded-[var(--fv-radius)] px-3 py-2',
                collapsed && 'md:hidden'
              )}
              title={user.email}
            >
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold uppercase text-white"
                aria-hidden
              >
                {accountInitial(user.email)}
              </div>
              <p className="min-w-0 flex-1 truncate text-sm text-[var(--fv-sidebar-text)]">{user.email}</p>
            </div>
          ) : null}
          <div className="space-y-0.5">
            <button
              type="button"
              onClick={() => void switchAccount()}
              className={cn(
                sidebarNavItemVariants(),
                'w-full border-s-2 border-transparent',
                collapsed && 'md:justify-center md:px-2'
              )}
              title={collapsed ? t('auth.switchAccount') : undefined}
              aria-label={t('auth.switchAccount')}
            >
              <UserRound className="h-4 w-4 shrink-0" aria-hidden />
              <span className={cn(collapsed && 'md:hidden')}>{t('auth.switchAccount')}</span>
            </button>
            <button
              type="button"
              onClick={() => void signOutUser()}
              className={cn(
                sidebarNavItemVariants(),
                'w-full border-s-2 border-transparent',
                collapsed && 'md:justify-center md:px-2'
              )}
              title={collapsed ? t('auth.signOut') : undefined}
              aria-label={t('auth.signOut')}
            >
              <LogOut className="h-4 w-4 shrink-0" aria-hidden />
              <span className={cn(collapsed && 'md:hidden')}>{t('auth.signOut')}</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
